// Drop-in shim mapping the Next.js `<Link href>` API onto TanStack Router's
// `<Link to>`. Lets ported primitives keep `import Link from "..."` and
// `<Link href="/path">…</Link>` syntax unchanged.
//
// Caveats vs real next/link:
//   - `prefetch` / `replace` are mapped 1:1 (TanStack supports both).
//   - `as` prop is dropped (not used in admin-nextjs codebase).
//   - External URLs (http/https/mailto/#) fall through to a plain <a>.
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink } from "@tanstack/react-router";

interface NextLinkCompatProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  href: string;
  replace?: boolean;
  prefetch?: boolean;
  scroll?: boolean;
  children?: ReactNode;
}

const isExternal = (href: string) =>
  /^(?:[a-z][a-z0-9+\-.]*:|#|mailto:|tel:)/i.test(href);

const NextLinkShim = forwardRef<HTMLAnchorElement, NextLinkCompatProps>(function NextLink(
  { href, replace, prefetch, scroll: _scroll, children, ...rest },
  ref,
) {
  if (isExternal(href)) {
    return (
      <a href={href} ref={ref} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <RouterLink
      to={href}
      replace={replace}
      preload={prefetch === false ? false : "intent"}
      ref={ref}
      {...rest}
    >
      {children}
    </RouterLink>
  );
});

export default NextLinkShim;
export { NextLinkShim as Link };
